import type { IconName } from '../../components/ui/Icon';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from '../../utils/axiosInstance';

export type BFStatus =
  | 'to_ask' | 'asked' | 'terms_received' | 'order_placed' | 'topup_placed' | 'closed' | 'not_participating';
export type Participates = 'unknown' | 'yes' | 'selection' | 'tolerated' | 'no';
export type Scope = 'unknown' | 'whole' | 'selection' | 'exclusions';
export type YesNo = 'unknown' | 'yes' | 'conditional' | 'no';
export type AskKey = 'discount' | 'period' | 'stock_tiers' | 'payment_days' | 'same_conditions' | 'topup';
export type AskState = 'open' | 'yes' | 'no';

export const STATUS_LABEL: Record<BFStatus, string> = {
  to_ask: 'To ask',
  asked: 'Asked',
  terms_received: 'Terms received',
  order_placed: 'Order placed',
  topup_placed: 'Top-up placed',
  closed: 'Closed',
  not_participating: 'Not participating',
};
export const STATUS_ORDER: BFStatus[] = [
  'to_ask', 'asked', 'terms_received', 'order_placed', 'topup_placed', 'closed', 'not_participating',
];

export const PARTICIPATES_LABEL: Record<Participates, string> = {
  unknown: 'Unknown',
  yes: 'Yes',
  selection: 'Selection only',
  tolerated: 'Tolerated, no campaign',
  no: 'No',
};
export const SCOPE_LABEL: Record<Scope, string> = {
  unknown: 'Unknown',
  whole: 'Whole collection',
  selection: 'Selection list',
  exclusions: 'Whole collection minus exclusions',
};
export const YESNO_LABEL: Record<YesNo, string> = {
  unknown: 'Unknown',
  yes: 'Yes',
  conditional: 'Conditional',
  no: 'No',
};

/** Robbie's six standard asks, in the order of his yearly email. */
/** The sections of a brand's terms, shared by the brand page (section
 *  titles, accent bars) and the register (column headers) so the same
 *  colour + icon means the same thing everywhere. */
export type BFSectionKey = 'contact' | 'sellout' | 'sellin' | 'topup' | 'notes';
export const BF_SECTIONS: Record<BFSectionKey, { label: string; icon: IconName; color: string; bg: string }> = {
  contact: { label: 'Contact & asks', icon: 'users', color: 'var(--sitewide)', bg: 'var(--sitewide-bg)' },
  sellout: { label: 'Discount for shoppers', icon: 'tag', color: 'var(--seasonal)', bg: 'var(--seasonal-bg)' },
  sellin: { label: 'Stock order', icon: 'inbox', color: 'var(--brand)', bg: 'var(--brand-bg)' },
  topup: { label: 'Top-up order', icon: 'trending', color: 'var(--accent-3)', bg: 'rgba(232,163,62,0.14)' },
  notes: { label: 'Notes', icon: 'archive', color: 'var(--accent-5)', bg: 'rgba(201,75,154,0.12)' },
};

export const ASKS: { key: AskKey; label: string; short: string }[] = [
  { key: 'discount', label: 'Consumer discount (recommended)', short: 'Discount' },
  { key: 'period', label: 'Promo period', short: 'Period' },
  { key: 'stock_tiers', label: 'Extra discount on stock order', short: 'Stock extra' },
  { key: 'payment_days', label: 'Payment terms 30+ days', short: '+30 days' },
  { key: 'same_conditions', label: 'Same terms through Cyber Monday', short: 'Same terms' },
  { key: 'topup', label: 'Top-up order after Black Friday', short: 'Top-up' },
];

export interface Tier {
  min: number;
  unit: 'eur' | 'pcs';
  extra_pct: number;
  note?: string;
}
export interface RegionalCap {
  region: string;
  max_pct: number;
}
export interface Quote {
  speaker: string;
  date: string;
  text: string;
}

export interface BFSeason {
  id: number;
  year: number;
  campaign: string | null;
  campaign_name?: string;
  prebf_start: string | null;
  prebf_end: string | null;
  week_start: string | null;
  week_end: string | null;
  notes: string;
  status_counts: Record<BFStatus, number>;
  updated_at: string;
}

/** Decimal fields come back from the API as strings ("25.00") or null. */
type Dec = string | null;

export interface PreviousSummary {
  year: number;
  status: BFStatus;
  our_discount_pct: Dec;
  discount_max_pct: Dec;
  extra_discount_pct: Dec;
  minimum_order_net: Dec;
  order_amount: Dec;
  topup_allowed: YesNo;
}

export interface BrandTerms {
  id: number;
  season: number;
  year: number;
  brand: string;
  brand_name: string;
  brand_logo: string;
  brand_color: string;
  brand_priority: string;
  status: BFStatus;
  owner: number | null;
  owner_name: string;
  contact: string;
  agent: string;
  asked_at: string | null;
  answered_at: string | null;
  brand_initiated: boolean;
  source_url: string;
  asks: Record<AskKey, AskState>;
  participates: Participates;
  discount_recommended_pct: Dec;
  discount_max_pct: Dec;
  regional_caps: RegionalCap[];
  promo_start: string | null;
  promo_end: string | null;
  scope: Scope;
  sellout_notes: string;
  /** Visibility the brand demands in return; non-empty = eye mark on the register. */
  visibility_conditions: string;
  our_discount_pct: Dec;
  our_promo_start: string | null;
  extra_discount_pct: Dec;
  tiers: Tier[];
  minimum_order_net: Dec;
  order_deadline: string | null;
  order_channel: string;
  payment_terms: string;
  credit_limit: Dec;
  same_conditions: YesNo;
  order_po: string;
  order_amount: Dec;
  sellin_notes: string;
  topup_allowed: YesNo;
  topup_deadline: string | null;
  topup_discount_pct: Dec;
  topup_minimum: Dec;
  topup_po: string;
  topup_amount: Dec;
  topup_notes: string;
  notes: string;
  quotes: Quote[];
  imported_from: string;
  /** Who filled what from where, oldest first. */
  change_log: { at: string; by: string; source: string; url: string; fields: string[] }[];
  discount_asset: number | null;
  discount_asset_title?: string;
  discount_asset_campaign?: string;
  previous: PreviousSummary | null;
  updated_at: string;
}

export type TermsPatch = Partial<Omit<BrandTerms,
  'id' | 'year' | 'brand_name' | 'brand_logo' | 'brand_color' | 'brand_priority' | 'owner_name'
  | 'imported_from' | 'change_log' | 'discount_asset' | 'discount_asset_title' | 'discount_asset_campaign' | 'previous' | 'updated_at'>>;

const seasonsKey = ['bf', 'seasons'] as const;
const seasonKey = (year: number) => ['bf', 'season', year] as const;
const termsKey = (year: number) => ['bf', 'terms', year] as const;
const brandHistoryKey = (brand: string) => ['bf', 'brand', brand] as const;

export const useBFSeasons = () =>
  useQuery<BFSeason[]>({
    queryKey: seasonsKey,
    queryFn: async () => (await axios.get('api/bf/seasons/')).data,
  });

export const useBFSeason = (year: number) =>
  useQuery<BFSeason | null>({
    queryKey: seasonKey(year),
    queryFn: async () => {
      try {
        return (await axios.get(`api/bf/seasons/${year}/`)).data;
      } catch (e) {
        if ((e as { response?: { status?: number } })?.response?.status === 404) return null;
        throw e;
      }
    },
  });

export const useBFTerms = (year: number) =>
  useQuery<BrandTerms[]>({
    queryKey: termsKey(year),
    queryFn: async () => (await axios.get(`api/bf/terms/?year=${year}`)).data,
  });

/** Every season for one brand, newest first — the prep card. */
export const useBrandBFHistory = (brand: string | undefined) =>
  useQuery<BrandTerms[]>({
    queryKey: brandHistoryKey(brand ?? ''),
    queryFn: async () => (await axios.get(`api/bf/terms/?brand=${encodeURIComponent(brand ?? '')}`)).data,
    enabled: !!brand,
  });

const useInvalidateBF = () => {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['bf'] });
};

export const useSeasonMutations = () => {
  const invalidate = useInvalidateBF();
  const create = useMutation({
    mutationFn: async (payload: Partial<BFSeason> & { year: number }): Promise<BFSeason> =>
      (await axios.post('api/bf/seasons/', payload)).data,
    onSuccess: invalidate,
  });
  const patch = useMutation({
    mutationFn: async ({ year, ...payload }: Partial<BFSeason> & { year: number }): Promise<BFSeason> =>
      (await axios.patch(`api/bf/seasons/${year}/`, payload)).data,
    onSuccess: invalidate,
  });
  const populate = useMutation({
    mutationFn: async (year: number): Promise<{ created: number }> =>
      (await axios.post(`api/bf/seasons/${year}/populate/`)).data,
    onSuccess: invalidate,
  });
  return { create, patch, populate };
};

export const useTermsMutations = () => {
  const qc = useQueryClient();
  const invalidate = useInvalidateBF();
  const patch = useMutation({
    mutationFn: async ({ id, ...payload }: TermsPatch & { id: number }): Promise<BrandTerms> =>
      (await axios.patch(`api/bf/terms/${id}/`, payload)).data,
    onSuccess: invalidate,
  });
  const create = useMutation({
    mutationFn: async (payload: { season: number; brand: string }): Promise<BrandTerms> =>
      (await axios.post('api/bf/terms/', payload)).data,
    onSuccess: invalidate,
  });
  const createPlacement = useMutation({
    mutationFn: async (id: number): Promise<BrandTerms> =>
      (await axios.post(`api/bf/terms/${id}/create-placement/`)).data,
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ['assets'] });
      qc.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });
  return { patch, create, createPlacement };
};

/* ---------- which year the section is on ---------- */

/** The register and the calendar share one "current year": switching the
 *  year on either page is remembered (per tab), so the sidebar links open
 *  the same season on both. */
const YEAR_KEY = 'bf-year';
export const rememberBFYear = (year: number) => {
  try { sessionStorage.setItem(YEAR_KEY, String(year)); } catch { /* private mode */ }
};
export const rememberedBFYear = (): number | null => {
  try {
    const v = parseInt(sessionStorage.getItem(YEAR_KEY) ?? '', 10);
    return Number.isFinite(v) ? v : null;
  } catch { return null; }
};

/* ---------- formatting helpers ---------- */

export const num = (v: Dec | number | undefined): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

export const fmtPct = (v: Dec | number | undefined) => {
  const n = num(v);
  return n === null ? '' : `${+n.toFixed(2)}%`;
};

export const fmtEurShort = (v: Dec | number | undefined) => {
  const n = num(v);
  if (n === null) return '';
  if (n >= 1_000_000) return `€${+(n / 1_000_000).toFixed(1)}M`;
  if (n >= 100_000) return `€${Math.round(n / 1000)}k`;
  if (n >= 1000) return `€${+(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k`;
  return `€${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "24 Nov" — with the year appended ("24 Nov 2025") whenever the date is
 *  not in the current year, so dates from earlier seasons can't be misread. */
export const fmtDay = (iso: string | null | undefined) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map((x) => parseInt(x, 10));
  const day = `${d} ${MONTHS[m - 1]}`;
  return y && y !== new Date().getFullYear() ? `${day} ${y}` : day;
};
export const fmtRange = (a: string | null, b: string | null) =>
  a || b ? `${fmtDay(a) || '?'} – ${fmtDay(b) || '?'}` : '';

export const tierLine = (t: Tier) =>
  `${t.unit === 'eur' ? fmtEurShort(t.min) : `${t.min} pcs`} → +${+t.extra_pct.toFixed(2)}%`;

/** One-line sell-out summary: "max 20% · EU 20 / Benelux 25 · 24 Nov – 1 Dec". */
export const selloutLine = (t: Pick<BrandTerms, 'discount_recommended_pct' | 'discount_max_pct' | 'regional_caps' | 'promo_start' | 'promo_end'>) => {
  const parts: string[] = [];
  if (num(t.discount_recommended_pct) !== null) parts.push(`rec ${fmtPct(t.discount_recommended_pct)}`);
  if (num(t.discount_max_pct) !== null) parts.push(`max ${fmtPct(t.discount_max_pct)}`);
  if (t.regional_caps?.length) parts.push(t.regional_caps.map((c) => `${c.region} ${c.max_pct}%`).join(' / '));
  const r = fmtRange(t.promo_start, t.promo_end);
  if (r) parts.push(r);
  return parts.join(' · ');
};

/** One-line sell-in summary: "+10% · 3 tiers from €30k · min €25k · by 20 Oct". */
export const sellinLine = (t: Pick<BrandTerms, 'extra_discount_pct' | 'tiers' | 'minimum_order_net' | 'order_deadline'>) => {
  const parts: string[] = [];
  if (num(t.extra_discount_pct) !== null) parts.push(`+${fmtPct(t.extra_discount_pct)}`);
  if (t.tiers?.length) {
    parts.push(t.tiers.length === 1 ? tierLine(t.tiers[0]) : `${t.tiers.length} tiers from ${t.tiers[0].unit === 'eur' ? fmtEurShort(t.tiers[0].min) : `${t.tiers[0].min} pcs`}`);
  }
  if (num(t.minimum_order_net) !== null) parts.push(`min ${fmtEurShort(t.minimum_order_net)}`);
  if (t.order_deadline) parts.push(`by ${fmtDay(t.order_deadline)}`);
  return parts.join(' · ');
};

/** First line(s) of an imported free-text block, for summaries when the
 *  structured fields are still empty (history imported from the sheet/emails). */
export const noteHead = (text: string | null | undefined, lines = 1, max = 90) =>
  (text ?? '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .slice(0, lines)
    .map((l) => (l.length > max ? `${l.slice(0, max - 1)}…` : l))
    .join('\n');

/* ---------- owners & history ---------- */

export interface BFOwner {
  id: number;
  name: string;
  email: string;
}

/** The five people who can own a brand (Robbie, Geoffrey, Doreen, Nathalie, Yann). */
export const useBFOwners = () =>
  useQuery<BFOwner[]>({
    queryKey: ['bf', 'owners'],
    queryFn: async () => (await axios.get('api/bf/owners/')).data,
    staleTime: 10 * 60_000,
  });

export interface HistoryRow {
  id: number;
  year: number;
  brand: string;
  brand_name: string;
  brand_logo: string;
  brand_color: string;
  brand_priority: string;
  status: BFStatus;
  participates: Participates;
  discount_recommended_pct: Dec;
  discount_max_pct: Dec;
  regional_caps: RegionalCap[];
  promo_start: string | null;
  promo_end: string | null;
  our_discount_pct: Dec;
  our_promo_start: string | null;
  extra_discount_pct: Dec;
  tiers: Tier[];
  minimum_order_net: Dec;
  order_deadline: string | null;
  payment_terms: string;
  order_amount: Dec;
  topup_allowed: YesNo;
  topup_deadline: string | null;
  sellin_head: string;
}

export const useBFHistory = () =>
  useQuery<HistoryRow[]>({
    queryKey: ['bf', 'history'],
    queryFn: async () => (await axios.get('api/bf/terms/history/')).data,
  });

/* ---------- perfy priority ---------- */

export interface BrandPriority {
  perfy_names: string[];
  sales_12m: number | null;
  orders_12m: number | null;
  product_margin_pct_12m: number | null;
  gross_margin_pct_12m: number | null;
  bf_sales: number | null;
  bf_orders: number | null;
  variants: number | null;
  variants_in_stock: number | null;
  discount_typical: number | null;
  discount_typical_stock: number | null;
}

export interface SeasonPriority {
  configured: boolean;
  error: string | null;
  bf_year: number;
  bf_window: { start_date: string; end_date: string };
  window_12m: { start_date: string; end_date: string } | null;
  margins_through: string | null;
  discounts_error: string | null;
  brands: Record<string, BrandPriority>;
}

export const useBFPriority = (year: number, enabled = true) =>
  useQuery<SeasonPriority>({
    queryKey: ['bf', 'priority', year],
    queryFn: async () => (await axios.get(`api/bf/seasons/${year}/priority/`)).data,
    enabled,
    staleTime: 60 * 60 * 1000,
  });

/** "10%" or "10% · stock 28%" — the typical discount shoppers see now. */
export const siteDiscountLine = (p?: BrandPriority) => {
  if (!p) return '';
  const a = p.discount_typical;
  const b = p.discount_typical_stock;
  if (a == null && b == null) return '';
  if (b == null || b === a) return `${a ?? 0}%`;
  return `${a ?? 0}% · stock ${b}%`;
};

/** Links found in a free-text field, with a readable label ("P33381 in Odoo", "Email thread"). */
export function findLinks(text: string | null | undefined): { url: string; label: string }[] {
  const urls = Array.from(new Set((text || '').match(/https?:\/\/[^\s<>"')]+/g) || []))
    .map((u) => u.replace(/[.,;:]+$/, ''));
  return urls.map((url) => {
    const po = url.match(/odoo\.com\/odoo\/purchase\/(\d+)/);
    if (po) return { url, label: `P${po[1]} in Odoo` };
    if (/odoo\.com/.test(url)) return { url, label: 'Open in Odoo' };
    if (/mail\.google\.com|superhuman\.com/.test(url)) return { url, label: 'Email thread' };
    let host = url;
    try { host = new URL(url).hostname.replace(/^www\./, ''); } catch { /* keep the raw url */ }
    return { url, label: host };
  });
}
